<template>
  <div class="container debug-mode">
    <div class="glass-card question-card">
      <div class="question-content" id="questionText">{{ problem }}</div>
      
      <!-- 题目信息 -->
      <div class="problem-info">
        <!-- 自定义题目入口 - 移到左边 -->
        <div class="problem-info-item custom-problem-link" 
             v-if="!isRecording && analysisStatus === null"
             @click="checkVipAndExecute(() => showCustomProblemModal = true)">
          点此自定义题目
        </div>
        <!-- 系统题目信息 - 移到右边 -->
        <div class="problem-info-item">
          {{ problemSource }}
        </div>
      </div>
    </div>

    <!-- 系统分析卡片 -->
    <div class="glass-card analysis-card">
      <div class="analysis-title">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
        </svg>
        DEEPSEEK分析
        <div class="model-selector">
          <label class="switch">
            <input type="checkbox" v-model="useR1Model">
            <span class="slider round"></span>
          </label>
          <span id="modelLabel">{{ selectedModelLabel }}</span>
        </div>
        <!-- 移除思考时长计时器 -->
      </div>
      <div class="analysis-content">
        <!-- 只在初始状态显示placeholder, AI禁止修改analysis-placeholder的样式及具体内容 -->
        <div v-if="analysisStatus === null" class="analysis-placeholder">
          ●思考完毕后，点击开始答题进行录音答题<br>
          ●答题完毕后，系统自动分析生成报告<br>
          ●AI点评的最有用的部分是致命漏洞，AI点评仅作参考，AI点评社会现象题型效果最佳<br>
        </div>
        <!-- 任何其他状态都显示分析内容，包括状态消息 -->
        <div v-else v-html="formattedAnalysis" class="analysis-result"></div>
        
        <!-- 轮询计数器显示 -->
        <div v-if="showPollCount" class="poll-counter">
          处理中... {{ currentPollCount }}/{{ maxPollCount }}
        </div>
        
        <!-- 分析内容中的控制按钮 -->
        <div class="analysis-controls" v-if="analysisStatus !== 'success' && analysisStatus !== 'loading' && analysisStatus !== 'uploading'">
          <button class="btn btn-primary" @click="checkVipAndExecute(startRecording)" v-if="!isRecording">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
              <line x1="12" y1="19" x2="12" y2="22"></line>
            </svg>
            开始答题
          </button>
          <button class="btn btn-warning" @click="checkVipAndExecute(pauseRecording)" v-if="isRecording && !isPaused">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="6" y="4" width="4" height="16"></rect>
              <rect x="14" y="4" width="4" height="16"></rect>
            </svg>
            暂停答题
          </button>
          <button class="btn btn-success" @click="checkVipAndExecute(resumeRecording)" v-if="isRecording && isPaused">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
            继续答题
          </button>
          <button class="btn btn-danger" @click="checkVipAndExecute(stopAndUpload)" v-if="isRecording && isPaused">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
            上传录音
          </button>
        </div>
        
        <!-- 下一题按钮 - 只在分析完成后显示 -->
        <div class="next-question-container" v-if="analysisStatus === 'success' && isContentFullyDisplayed">
          <button class="btn btn-next-question" @click="showNextQuestionConfirm">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h14"></path>
              <path d="m12 5 7 7-7 7"></path>
            </svg>
            下一题
          </button>
        </div>
      </div>
    </div>

    <!-- 录音指示器 -->
    <div class="recording-indicator" :class="{ active: isRecording && !isPaused }">
      <div class="pulse"></div>
      <span>正在录音</span>
      <div class="recording-time">
        <span class="time-label">录音时长</span>
        <span class="time-value">{{ formatTime(recordingTime) }}</span>
      </div>
    </div>
    
    <!-- 聊天输入框 -->
    <div class="chat-container" v-if="showChatInput || analysisStatus === 'success'">
      <chat-input 
        ref="chatInput"
        placeholder="输入追问..."
        :disabled="followUpStatus === 'sending'"
        @send="directSendFollowUp"
        v-model="followUpMessage"
      />
      <button class="fallback-send-btn" @click="manualSend" v-if="followUpMessage && followUpMessage.trim()">
        发送
      </button>
    </div>
    
    <!-- 自定义题目模态框 -->
    <div class="modal-overlay" v-if="showCustomProblemModal" @click.self="showCustomProblemModal = false">
      <div class="modal-content">
        <h3>自定义题目</h3>
        <textarea 
          v-model="customProblem" 
          placeholder="只建议输入社会现象题型，其它题型效果可能不佳，请输入题目..." 
          rows="6"
        ></textarea>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showCustomProblemModal = false">取消</button>
          <button class="btn btn-primary" @click="submitCustomProblem" :disabled="!customProblem.trim() || isSubmittingCustomProblem">
            提交
          </button>
        </div>
      </div>
    </div>
    
    <!-- 下一题确认模态框 -->
    <div class="modal-overlay" v-if="showNextQuestionModal" @click.self="showNextQuestionModal = false">
      <div class="modal-content">
        <h3>进入下一题</h3>
        <p>进入下一题将清空本次的记录，是否确认继续？</p>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showNextQuestionModal = false">取消</button>
          <button class="btn btn-primary" @click="proceedToNextQuestion">确认</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AIService from '@/services/ai.service';
import { marked } from 'marked'; // 导入marked库
import UserService from '@/services/user.service'; // 导入UserService
import QuestionCard from '@/components/common/QuestionCard.vue';
import ChatInput from '@/components/common/ChatInput.vue';
import Timer from '@/components/common/Timer.vue';
import AudioControls from '@/components/common/AudioControls.vue';
// 引入样式文件
import '@/styles/ai-mode.css';

export default {
  name: 'AIMode',
  components: {
    QuestionCard,
    ChatInput,
    Timer,
    AudioControls
  },
  data() {
    return {
      // 题目信息
      problem: '正在加载题目...',
      problemYear: '',
      problemType: '',
      problemSource: '',
      
      // 录音相关
      isRecording: false,
      isPaused: false,
      recordingTime: 0,
      thinkingTime: 0, // 添加思考时长
      recorder: null,
      audioChunks: [],
      recordingInterval: null,
      thinkingInterval: null, // 添加思考时长计时器
      isMicrophoneAvailable: true,
      
      // 移除文本输入相关变量 - 本系统仅支持语音回答题目
      // textInput: '', // 已移除
      
      // 分析状态
      analysisStatus: null, // loading, uploading, success, error
      analysisError: '',
      analysisResult: '',
      statusFilePath: '',
      analysisFilePath: '',
      showChatInput: false, // 添加一个控制聊天输入框显示的变量
      
      // 追问相关
      followUpMessage: '',
      followUpStatus: null, // sending, success, error
      
      // 模型选择
      useR1Model: true,

      // 内容是否已完全展示的标记
      isContentFullyDisplayed: false,
      
      // 自定义题目相关
      customProblem: '',
      isSubmittingCustomProblem: false,
      showCustomProblemModal: false,
      
      // VIP相关
      isVip: false,
      vipChecked: false,
      showVipModal: false,
      
      // 新增问题ID
      problemId: null,
      
      // 添加用户交互检测状态
      _userInteracted: false,
      
      // 下一题相关
      showNextQuestionModal: false,
      
      // 添加轮询计数显示
      showPollCount: false,
      currentPollCount: 0,
      maxPollCount: 60,
    };
  },
  computed: {
    selectedModel() {
      return this.useR1Model ? 'deepseek-r1' : 'deepseek-v3';
    },
    selectedModelLabel() {
      return this.useR1Model ? 'R1模型' : 'V3模型';
    },
    formattedAnalysis() {
      if (!this.analysisResult) return '';
      
      // 已经在getAnalysisFile方法中处理为HTML，直接返回
      let result = this.analysisResult;
      //不展示DEEPSEEK:
      result = result.replaceAll("DEEPSEEK：","")
      
      // 正则表达式匹配所有【】括号中的内容
      const bracketPattern = /【(.+?)】/g;
      
      // 先替换**提出对策**这样的粗体标记
      result = result.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      
      // 处理带序号的列表项，确保不会破坏markdown渲染
      result = result.replace(/(\d+\.\s+)(<strong>.*?<\/strong>)/g, '<span class="list-item">$1$2</span>');
      
      // 给所有【】包裹的内容添加特殊样式类，并确保它们都左对齐
      result = result.replace(bracketPattern, match => {
        const label = match.substring(1, match.length - 1); // 去掉【】
        
        // 特殊处理【DEEPSEEK分析】、【语音识别】和【用时】使其字体更大
        if (label === 'DEEPSEEK分析' || label === '语音识别' || label === '用时') {
          return `<div class="deepseek-section"><span class="deepseek-analysis-title">【${label}】</span></div>`;
        }
        
        // 对所有其他【】标签使用统一样式
        return `<div class="deepseek-section"><span class="bracketed-content">【${label}】</span></div>`;
      });
      
      // 确保在markdown格式的内容前能正确关闭deepseek-section
      result = result.replace(/<\/div>(\s*)<p>/g, '</div>$1<p style="text-align:left;">');
      
      return result;
    }
  },
  mounted() {
    // 获取随机问题
    this.loadRandomProblem();
    
    // 初始化思考时长计时器，用户进入页面就开始计时
    this.startThinkingTimer();
    
    // 添加页面点击事件监听器，用户交互时才验证登录
    document.addEventListener('click', this.handlePageInteraction);
  },
  beforeUnmount() {
    // 停止计时器（如果存在）
    if (this.recordingInterval) {
      clearInterval(this.recordingInterval);
    }
    
    // 停止麦克风录制（如果正在进行）
    if (this.recorder && this.recorder.state === 'recording') {
      this.recorder.stop();
    }
    
    // 移除页面点击事件监听器
    document.removeEventListener('click', this.handlePageInteraction);
  },
  methods: {
    // 格式化时间显示 (mm:ss)
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    },
    
    // 检查网络状况和请求
    checkNetworkStatus() {
      console.log('==== 检查网络状况 ====');
      console.log('在线状态: ', navigator.onLine ? '在线' : '离线');
      
      // 尝试ping API服务器
      const testUrl = `${process.env.VUE_APP_API_BASE_URL || 'https://ddrl.xyz/api'}/ping`;
      console.log(`尝试ping API服务器: ${testUrl}`);
      
      fetch(testUrl, { method: 'GET', mode: 'no-cors', cache: 'no-cache' })
        .then(() => {
          console.log('API服务器可以访问');
        })
        .catch(error => {
          console.error('无法访问API服务器: ', error);
        });
        
      // 输出当前路径信息
      console.log('当前分析文件路径: ', this.analysisFilePath);
    },
    
    // 直接发送追问
    directSendFollowUp(message) {
      // 优先使用传入的消息参数，保证使用最新内容
      let messageToSend = '';
      
      if (message && typeof message === 'string') {
        messageToSend = message;
      } else if (this.followUpMessage) {
        messageToSend = this.followUpMessage;
      }

      
      if (!messageToSend || !messageToSend.trim()) {
        console.log('追问内容为空，无法发送');
        return;
      }
      
      // 检查是否正在发送追问
      if (this.followUpStatus === 'sending') {
        console.log('正在发送追问，请等待完成');
        return;
      }
      
      // 检查分析状态，必须是success才能发送追问
      if (this.analysisStatus !== 'success') {
        console.log('分析尚未完成，不能发送追问');
        alert('请等待当前分析完成后再发送追问');
        return;
      }
      
      // 检查内容是否完全展示完毕
      if (!this.isContentFullyDisplayed) {
        console.log('上一次回复尚未完全展示，请等待');
        alert('请等待上一次回复内容完全展示后再发送追问');
        return;
      }
      
      // 设置将要发送的消息
      this.followUpMessage = messageToSend;
      
      // 直接调用发送处理函数，不经过VIP检查
      this.proceedWithFollowUp();
    },
    
    checkMicrophoneAvailability() {
      // 检查是否在安全上下文中（HTTPS）
      if (!window.isSecureContext) {
        console.error("页面不在安全上下文中运行，MediaDevices API可能不可用");
        this.isMicrophoneAvailable = false;
        return;
      }
      
      // 检查浏览器是否支持mediaDevices API
      if (!navigator.mediaDevices) {
        console.error("navigator.mediaDevices 不可用");
        this.isMicrophoneAvailable = false;
        return;
      }
      
      // 尝试获取麦克风权限
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => {
          console.log("成功获取麦克风权限");
          this.isMicrophoneAvailable = true;
          // 释放媒体流
          stream.getTracks().forEach(track => track.stop());
        })
        .catch(error => {
          console.error('获取麦克风权限失败:', error);
          this.isMicrophoneAvailable = false;
        });
    },
    
    // 检查VIP状态
    checkVipStatus() {
      // 检查用户是否已登录
      const user = localStorage.getItem('user');
      if (!user) {
        window.dispatchEvent(new CustomEvent('auth-error', { 
          detail: { loginRequired: true, message: '请先登录以继续操作' } 
        }));
        return;
      }
      
      this.vipChecked = true;
      
      UserService.checkVipStatus()
        .then(data => {
          this.isVip = data.isVip;
          // 如果不是VIP用户，显示VIP模态框
          if (!this.isVip) {
            this.showVipModal = true;
            // 弹出提示
            alert('该功能仅限VIP用户使用，请开通VIP会员！');
          }
        })
        .catch(error => {
          // 忽略认证错误，因为这些错误会由request.js统一处理
          if (error.isAuthError) {
            console.log('忽略认证错误:', error.message);
            return;
          }
          
          if (error.statusCode === 401 && error.loginRequired) {
            console.log('用户未登录，显示登录模态窗口');  
            window.dispatchEvent(new CustomEvent('auth-error', { 
              detail: { loginRequired: true, message: '请先登录以继续操作' } 
            }));
          }else{
            console.error('检查VIP状态出错:', error);
            alert('检查VIP状态时出错，请稍后重试');
          }
          // 出错时默认不是VIP
          this.isVip = false;
          this.showVipModal = true;
        });
    },
    
    loadRandomProblem() {
      AIService.getRandomProblem()
        .then(data => {
          if (data && data.success) {
            // 更新问题内容
            this.problem = data.detail;
            this.problemYear = data.year;
            this.problemType = data.type;
            // 修改，不显示province字段
            this.problemSource = data.source;
            
            // 保存问题ID，以便后续提交录音时使用
            this.problemId = data.pid;
            
            console.log('成功加载随机题目:', data);
          } else {
            console.error('获取随机题目失败:', data);
            this.problem = '加载题目失败，请刷新页面重试。';
          }
        })
        .catch(error => {
          console.error('Error loading problem:', error);
          if (error.response && error.response.data && error.response.data.message) {
            this.problem = error.response.data.message;
          } else {
            this.problem = '加载题目失败，请刷新页面重试。';
          }
        });
    },
    
    startRecording() {
      console.log("开始录音函数被调用");
      
      // 停止思考时长计时，此时已记录了思考时长
      this.stopThinkingTimer();
      
      // 检查MediaRecorder是否可用
      if (typeof MediaRecorder === 'undefined') {
        console.error("MediaRecorder API不可用");
        alert("您的浏览器不支持MediaRecorder API，无法进行录音。请使用最新版本的Chrome、Firefox或Edge浏览器。");
        return;
      }
      
      // 请求麦克风权限并设置采样率为16000Hz
      const audioConstraints = {
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      };
      
      navigator.mediaDevices.getUserMedia(audioConstraints)
        .then(stream => {
          console.log("成功获取麦克风权限");
          
          try {
            // 配置MediaRecorder，使用WAV格式
            const options = { 
              mimeType: 'audio/wav',
              audioBitsPerSecond: 256000 
            };
            
            // 如果浏览器不支持WAV格式，尝试使用WebM格式
            if (!MediaRecorder.isTypeSupported('audio/wav')) {
              console.log("浏览器不支持WAV格式录音，使用WebM格式");
              options.mimeType = 'audio/webm';
            }
            
            this.recorder = new MediaRecorder(stream, options);
            console.log("MediaRecorder实例创建成功，使用格式：", options.mimeType);
            this.audioChunks = [];
            
            // 收集录音数据
            this.recorder.ondataavailable = (event) => {
              this.audioChunks.push(event.data);
            };
            
            // 开始录音
            this.recorder.start();
            console.log("录音开始");
            this.isRecording = true;
            this.isPaused = false;
            
            // 重置计时并开始
            this.recordingTime = 0;
            if (this.$refs.recordingTimer) {
              this.$refs.recordingTimer.start();
              console.log("录音计时器已启动");
            } else {
              console.warn("录音计时器引用不存在");
              // 使用备用计时方法
              this.recordingInterval = setInterval(() => {
                this.recordingTime++;
                console.log("录音时间:", this.recordingTime);
              }, 1000);
            }
          } catch (e) {
            console.error("创建MediaRecorder实例失败:", e);
            alert("创建录音器失败: " + e.message);
          }
        })
        .catch(error => {
          console.error('获取麦克风权限失败:', error);
          
          let errorMessage = '无法访问麦克风，请确保已授予麦克风权限。';
          
          // 根据错误类型提供更具体的提示
          if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
            errorMessage = '麦克风访问被拒绝。请在浏览器设置中允许访问麦克风，然后刷新页面重试。';
          } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
            errorMessage = '未找到麦克风设备。请确保您的设备有麦克风并且正常工作。';
          } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
            errorMessage = '无法读取麦克风。可能是因为麦克风被其他应用程序占用，请关闭其他使用麦克风的应用后重试。';
          } else if (error.name === 'SecurityError') {
            errorMessage = '安全错误。请确保您通过HTTPS访问此页面。';
          }
          
          alert(errorMessage + '\n\n技术详情: ' + error.name + ' - ' + error.message);
        });
    },
    
    pauseRecording() {
      if (this.recorder && this.isRecording) {
        this.recorder.pause();
        this.isPaused = true;
        
        // 暂停计时器
        if (this.$refs.recordingTimer) {
          this.$refs.recordingTimer.pause();
        }
        // 如果使用的是备用计时方法，清除计时器
        if (this.recordingInterval) {
          clearInterval(this.recordingInterval);
          this.recordingInterval = null; // 将计时器引用设为null，确保恢复时能创建新计时器
        }
      }
    },
    
    resumeRecording() {
      if (this.recorder && this.isRecording && this.isPaused) {
        this.recorder.resume();
        this.isPaused = false;
        
        // 继续计时器
        if (this.$refs.recordingTimer) {
          this.$refs.recordingTimer.resume();
        } 
        // 如果使用的是备用计时方法，重新启动计时器
        else if (!this.recordingInterval) {
          this.recordingInterval = setInterval(() => {
            this.recordingTime++;
          }, 1000);
        }
      }
    },
    
    stopAndUpload() {
      if (this.recorder) {
        this.recorder.stop();
        this.isRecording = false;
        this.isPaused = false;
        
        // 停止计时器
        if (this.$refs.recordingTimer) {
          this.$refs.recordingTimer.pause();
        }
        // 如果使用的是备用计时方法，清除计时器
        if (this.recordingInterval) {
          clearInterval(this.recordingInterval);
          this.recordingInterval = null;
        }

        // 立即隐藏控制按钮并更新状态
        this.analysisStatus = 'loading';
        // 显示聊天输入框
        this.showChatInput = true;
        
        // 初始化分析结果并添加上传状态消息
        this.analysisResult = '';
        this.updateAnalysisContent('正在上传录音...');
        
        // 创建Blob并上传
        this.recorder.onstop = () => {
          // 检查录音格式
          let audioType = this.recorder.mimeType;
          let fileExtension = 'wav';
          
          if (!audioType.includes('audio/wav')) {
            console.log("使用非WAV格式录音，MIME类型:", audioType);
            fileExtension = 'webm';
          }
          
          const audioBlob = new Blob(this.audioChunks, { type: audioType });
          const formData = new FormData();
          formData.append('audio', audioBlob, `recording.${fileExtension}`);
          formData.append('model', this.selectedModel);
          // 添加思考时间和答题时间
          formData.append('think_time', this.thinkingTime);
          formData.append('ans_time', this.recordingTime);
          
          // 区分系统题目和自定义题目
          if (this.problemId) {
            // 检查题目来源判断类型
            if (this.problemSource === '自定义题目') {
              // 自定义题目
              formData.append('custom_problem_id', this.problemId);
              formData.append('problem_type', 'custom');
              console.log('使用自定义题目ID:', this.problemId);
            } else {
              // 系统题目
              formData.append('problem_id', this.problemId);
              formData.append('problem_type', 'system');
              console.log('使用系统题目ID:', this.problemId);
            }
          } else if (this.problem && this.problem !== '正在加载题目...') {
            // 如果没有ID但有题目内容，作为自定义题目发送
            formData.append('question', this.problem);
          }

          // 使用AIService上传文件
          AIService.uploadAudio(formData)
            .then(data => {
              // 保存分析文件路径，供后续使用
              this.statusFilePath = data.status_file;
              this.analysisFilePath = data.analyze_file;
              
              // 更新状态消息
              this.analysisResult = '';
              this.updateAnalysisContent('录音上传完毕，正在进行语音识别...');
              
              // 轮询获取识别文本
              this.pollTextStatus();
            })
            .catch(error => {
              this.analysisStatus = 'error';
              this.analysisError = error.message || '上传失败，请重试';
              // 显示错误消息
              this.analysisResult = '';
              this.updateAnalysisContent(`上传失败: ${error.message || '请重试'}`);
            });
        };
      }
    },
    
    pollTextStatus() {
      if (!this.statusFilePath) return;
      
      let pollCount = 0;
      const maxPollCount = 60; // 最多轮询60次
      
      // 初始化轮询计数显示
      this.currentPollCount = 10;
      this.maxPollCount = maxPollCount;
      this.showPollCount = true;
      
      const fetchTextStatus = () => {
        pollCount++;
        console.log(`[文本识别] 轮询第 ${pollCount} 次`);
        
        // 每10次轮询更新一次显示的计数，从10开始
        if (pollCount % 10 === 0) {
          this.currentPollCount = pollCount;
        } else if (pollCount === 1) {
          // 第一次轮询时显示为10
          this.currentPollCount = 10;
        }
        
        AIService.getTextStatus(this.statusFilePath)
          .then(data => {
            const status = data.status;
            
            if (status === 'completed') {
              console.log('[文本识别] 状态显示识别已完成，开始获取分析结果');
              
              // 语音识别完成，显示等待分析状态，不添加用时信息
              this.updateAnalysisContent('语音识别完成，正在分析...');
              this.analysisStatus = 'loading';
              
              // 已经完成语音识别，可以显示聊天输入框
              this.showChatInput = true;
              this.showPollCount = false; // 隐藏轮询计数
              this.getAnalysisFile();
            } else if (status.startsWith('failed:') || status.startsWith('error:')) {
              console.error('[文本识别] 处理失败:', status);
              this.analysisResult = '';
              this.updateAnalysisContent(`处理失败: ${status.split(':')[1] || '识别失败'}`);
              this.showPollCount = false; // 隐藏轮询计数
              throw new Error(status.split(':')[1] || '识别失败');
            } else if (status === 'processing') {
              console.log('[文本识别] 正在处理中...');
              if (pollCount < maxPollCount) {
                setTimeout(fetchTextStatus, 1000); // 缩短轮询间隔到1秒，加快响应速度
              } else {
                console.log('[文本识别] 超出最大轮询次数');
                this.updateAnalysisContent('文本识别超时，正在尝试获取分析结果...');
                this.analysisStatus = 'loading';
                this.showPollCount = false; // 隐藏轮询计数
                this.getAnalysisFile(); // 即使超时也尝试获取分析结果
              }
            } else {
              // 未知状态，继续轮询
              if (pollCount < maxPollCount) {
                setTimeout(fetchTextStatus, 1000); // 缩短轮询间隔
              } else {
                this.analysisStatus = 'loading';
                this.updateAnalysisContent('处理超时，正在尝试获取分析结果...');
                this.showPollCount = false; // 隐藏轮询计数
                this.getAnalysisFile(); // 即使状态未知也尝试获取分析结果
              }
            }
          })
          .catch(error => {
            console.error('[文本识别] 请求异常:', error);
            
            if (pollCount < maxPollCount) {
              setTimeout(fetchTextStatus, 1000); // 缩短轮询间隔
            } else {
              this.analysisStatus = 'loading';
              this.updateAnalysisContent('请求异常，正在尝试获取分析结果...');
              this.showPollCount = false; // 隐藏轮询计数
              this.getAnalysisFile(); // 即使出错也尝试获取分析结果
            }
          });
      };
      
      // 开始轮询
      setTimeout(fetchTextStatus, 500); // 缩短初始等待时间
    },
    
    getAnalysisFile() {
      // 如果没有分析文件路径，则退出
      if (!this.analysisFilePath) {
        this.analysisStatus = 'error';
        this.analysisError = '无法获取分析文件路径';
        return;
      }
      
      console.log(`[DEEPSEEK分析] 开始轮询分析结果`);
      
      // 记录上一次的内容长度，用于检测是否有新内容
      let lastContentLength = 0;
      // 记录轮询次数，用于调试
      let pollCount = 0;
      // 记录最后一次有新内容的时间
      let lastUpdateTime = Date.now();
      // 记录无内容轮询次数
      let emptyContentCount = 0;
      // 最大无更新时间（毫秒），超过这个时间没有更新就认为分析完成
      const maxIdleTime = 10000; // 10秒没有更新则认为完成
      // 最大轮询次数
      const maxPollCount = 60; // 60次最多轮询
      
      // 初始化轮询计数显示
      this.currentPollCount = 10;
      this.maxPollCount = maxPollCount;
      this.showPollCount = true;
      
      // 格式化时间函数
      const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}分${remainingSeconds}秒`;
      };
      
      // 创建一个函数来获取文件内容
      const fetchAnalysisFile = () => {
        pollCount++;
        console.log(`[DEEPSEEK分析] 轮询第 ${pollCount} 次`);
        
        // 每10次轮询更新一次显示的计数，从10开始
        if (pollCount % 10 === 0) {
          this.currentPollCount = pollCount;
        } else if (pollCount === 1) {
          // 第一次轮询时显示为10
          this.currentPollCount = 10;
        }
        
        // 每次请求都添加强制刷新参数，确保获取最新内容
        const forceRefresh = true;
        
        AIService.getAnalysisFile(this.analysisFilePath, forceRefresh)
          .then(data => {
            // 优先检查完成状态
            const finishStatus = data.finish_status || {};
            const isFinished = finishStatus.finished || false;
            const finishReason = finishStatus.finish_reason || null;
            const hasResponse = finishStatus.has_response || false;
            
            // 根据后端返回的完成状态判断是否完成
            if (isFinished || hasResponse) {
              console.log(`[DEEPSEEK分析] 后端返回已完成标志，原因: ${finishReason || '完成响应'}`);
              this.analysisStatus = 'success';
              this.showPollCount = false; // 隐藏轮询计数
              
              // 立即获取最终完整内容
              AIService.getAnalysisFile(this.analysisFilePath, true)
                .then(finalResponse => {
                  console.log(`[DEEPSEEK分析] 获取最终完整内容，长度: ${finalResponse.content.length} 字符`);
                  try {
                    // 处理最终内容
                    let modifiedContent = finalResponse.content;
                    
                    const htmlContent = marked(modifiedContent, {
                      breaks: true,
                      gfm: true,
                      headerIds: false,
                      mangle: false
                    });
                    
                    this.analysisResult = htmlContent;
                    
                    // 设置内容已完全展示的标记
                    this.isContentFullyDisplayed = true;
                    console.log('[DEEPSEEK分析] 内容已完全展示，可以发送追问');
                  } catch (err) {
                    console.error(`[DEEPSEEK分析] 处理最终内容时出错: ${err.message}`);
                    this.analysisResult = finalResponse.content;
                    // 即使出错也标记为完成
                    this.isContentFullyDisplayed = true;
                  }
                  
                  // 确保聊天输入框显示
                  this.showChatInput = true;
                })
                .catch(err => console.error(`[DEEPSEEK分析] 获取最终内容失败: ${err.message}`));
              
              return;
            }
            
            // 如果响应中包含'unchanged'字段，表示文件内容未变化，跳过处理
            if (data.unchanged) {
              console.log(`[DEEPSEEK分析] 文件内容未变化，继续轮询`);
              
              // 更新最后轮询时间的差值
              const idleTime = Date.now() - lastUpdateTime;
              
              // 如果长时间未更新(超过5秒)，判断为可能已完成
              if (idleTime > 5000 && pollCount > 5) {
                console.log(`[DEEPSEEK分析] 内容长时间未变化(${Math.floor(idleTime/1000)}秒)，可能已完成`);
                this.analysisStatus = 'success';
                this.isContentFullyDisplayed = true;
                this.showChatInput = true;
                this.showPollCount = false; // 隐藏轮询计数
                return;
              }
              
              let shouldContinuePolling = pollCount < maxPollCount && idleTime < maxIdleTime;
              
              if (shouldContinuePolling) {
                // 根据空闲时间调整轮询间隔
                const pollInterval = Math.min(1000 + Math.floor(idleTime / 1000) * 200, 3000);
                setTimeout(fetchAnalysisFile, pollInterval);
              } else {
                console.log(`[DEEPSEEK分析] 停止轮询，内容未变更已达到阈值，共${pollCount}次`);
                this.analysisStatus = 'success';
                this.isContentFullyDisplayed = true;
                this.showChatInput = true;
                this.showPollCount = false; // 隐藏轮询计数
              }
              return;
            }
            
            // 检查内容是否有更新
            const currentContent = data.content;
            const isBeingWritten = data.is_being_written || false;
            
            // 获取完成状态信息已在前面处理，这里不再重复
            
            if (currentContent.length === 0) {
              emptyContentCount++;
            } else {
              emptyContentCount = 0;
            }
            
            const hasNewContent = currentContent.length > lastContentLength;
            
            // 如果有新内容，更新最后更新时间
            if (hasNewContent || pollCount === 1) {
              lastUpdateTime = Date.now();
              console.log(`[DEEPSEEK分析] 检测到内容更新，长度: ${currentContent.length}`);
              
              // 使用marked处理内容并转换为HTML，与追问逻辑保持一致
              try {
                // 检查内容中是否包含语音识别标签和DEEPSEEK分析标签
                let modifiedContent = currentContent;
                
                // 不再在前端插入用时信息，只保留后端传递的内容
                const htmlContent = marked(modifiedContent, {
                  breaks: true,
                  gfm: true,
                  headerIds: false,
                  mangle: false
                });
                
                this.analysisResult = htmlContent;
                
                // 添加自动滚动到底部的功能
                this.$nextTick(() => {
                  const analysisContent = document.querySelector('.analysis-content');
                  if (analysisContent) {
                    analysisContent.scrollTop = analysisContent.scrollHeight;
                  }
                });
              } catch (error) {
                console.error(`[DEEPSEEK分析] 处理内容时出错: ${error.message}`);
                // 出错时使用原始内容
                this.analysisResult = currentContent;
              }
              this.analysisStatus = 'loading';
            }
            
            // 更新上次内容长度
            lastContentLength = currentContent.length;
            
            // 计算自上次更新以来的时间
            const idleTime = Date.now() - lastUpdateTime;
            
            // 判断是否应该继续轮询
            let shouldContinuePolling = false;
            
            if (isFinished) {
              // 如果元数据标记已完成，显示详细信息并停止轮询
              console.log(`[DEEPSEEK分析] 接收到完成标志，原因: ${finishReason}`);
              shouldContinuePolling = false;
            } else if (pollCount >= maxPollCount) {
              // 达到最大轮询次数
              console.log(`[DEEPSEEK分析] 达到最大轮询次数 ${maxPollCount}`);
              shouldContinuePolling = false;
            } else if (isBeingWritten) {
              // 文件仍在被写入
              shouldContinuePolling = true;
            } else if (idleTime < maxIdleTime) {
              // 在空闲时间内
              shouldContinuePolling = true;
            } else if (currentContent.length === 0 && emptyContentCount < 5) {
              // 内容为空但次数较少(减少阈值)
              shouldContinuePolling = true;
            } else {
              // 默认停止轮询
              console.log(`[DEEPSEEK分析] 达到停止轮询条件: 空闲 ${Math.round(idleTime/1000)}秒, 轮询 ${pollCount} 次`);
              shouldContinuePolling = false;
            }
            
            if (shouldContinuePolling) {
              // 根据内容是否更新调整轮询频率
              const pollInterval = hasNewContent ? 200 : Math.min(500 + Math.floor(idleTime / 1000) * 100, 1500);
              setTimeout(fetchAnalysisFile, pollInterval);
            } else {
              // 分析完成
              console.log(`[DEEPSEEK分析] 轮询完成，总共轮询 ${pollCount} 次，完成原因: ${isFinished ? finishReason : '超时/达到轮询上限'}`);
              this.analysisStatus = 'success';
              
              // 确保获取最终完整的分析结果
              AIService.getAnalysisFile(this.analysisFilePath, true)
                .then(finalResponse => {
                  console.log(`[DEEPSEEK分析] 获取最终完整内容，长度: ${finalResponse.content.length} 字符`);
                  try {
                    // 检查最终内容中是否包含语音识别标签和DEEPSEEK分析标签
                    let modifiedContent = finalResponse.content;
                    
                    // 不再在前端插入用时信息，直接使用后端提供的内容
                    const htmlContent = marked(modifiedContent, {
                      breaks: true,
                      gfm: true,
                      headerIds: false,
                      mangle: false
                    });
                    
                    this.analysisResult = htmlContent;
                    
                    // 设置内容已完全展示的标记
                    this.isContentFullyDisplayed = true;
                    console.log('[DEEPSEEK分析] 内容已完全展示，可以发送追问');
                  } catch (err) {
                    console.error(`[DEEPSEEK分析] 处理最终内容时出错: ${err.message}`);
                    this.analysisResult = finalResponse.content;
                    // 即使出错也标记为完成，因为已经获取了最终内容
                    this.isContentFullyDisplayed = true;
                  }
                  
                  // 确保聊天输入框显示
                  this.showChatInput = true;
                })
                .catch(err => {
                  console.error(`[DEEPSEEK分析] 获取最终内容失败: ${err.message}`);
                  // 即使获取最终内容失败，也标记为完成状态
                  this.isContentFullyDisplayed = true;
                  this.showChatInput = true;
                });
            }
          })
          .catch(error => {
            console.log("获取分析文件失败: ", error);
            
            // 出错时也继续尝试几次
            if (pollCount < 10) {
              setTimeout(fetchAnalysisFile, 1000);
            } else {
              this.analysisStatus = 'error';
              this.analysisError = error.message || '获取分析结果失败';
              // 即使出错也允许用户发送追问
              this.isContentFullyDisplayed = true;
              this.showChatInput = true;
            }
          });
      };
      
      // 开始轮询前先等待一小段时间，确保文件已创建
      setTimeout(fetchAnalysisFile, 100);
    },
    
    // 将原来的sendFollowUp函数内容移到这个新函数中
    proceedWithFollowUp() {
      if (!this.followUpMessage || !this.followUpMessage.trim()) {
        console.log('追问内容为空，取消发送');
        return;
      }
      
      this.followUpStatus = 'sending';
      
      // 获取当前的HTML内容
      const currentHtml = this.analysisResult || '';
      
      // 创建追问的Markdown文本
      let followUpMarkdown = '\n\n---\n\n';
      followUpMarkdown += '**我**：' + this.followUpMessage + '\n\n';
      followUpMarkdown += '**DEEPSEEK**：*思考中...*';
      
      // 使用marked将Markdown转换为HTML并附加到现有内容
      const appendedHtml = currentHtml + marked(followUpMarkdown, { 
        breaks: true, 
        gfm: true,
        headerIds: false,
        mangle: false 
      });
      
      // 保存原始完整内容，以便在失败时恢复
      const originalContent = this.analysisResult;
      const originalMessage = this.followUpMessage;
      
      // 更新UI显示，添加用户问题和思考中状态
      this.analysisResult = appendedHtml;
      
      // 记录当前滚动位置
      const analysisContent = document.querySelector('.analysis-content');
      const scrollPosition = analysisContent ? analysisContent.scrollTop : 0;
      
      AIService.sendFollowUp(this.followUpMessage, this.selectedModel)
        .then(data => {
          if (data && data.success) {
            this.followUpStatus = 'success';
            
            // 保存新的分析文件路径
            this.analysisFilePath = data.response_path;
            
            // 重置追问状态
            this.followUpMessage = '';
            this.followUpButtonText = '取消';
            this.followUpStatus = 'finished';
            
            // 清空输入框 - 数据层面
            this.followUpMessage = '';
            
            // 清空输入框 - DOM层面
            this.clearChatInput();
            
            // 开始轮询获取回复内容
            this.pollFollowUpResponse();
            
            // 滚动到底部，显示新内容
            setTimeout(() => {
              if (analysisContent) {
                analysisContent.scrollTop = analysisContent.scrollHeight;
              }
            }, 100);
          } else {
            // 恢复原始内容
            this.analysisResult = originalContent;
            
            if (data.message) {
              // 检查是否是会话限制相关的错误
              if (data.message.includes('会话超过限制') || 
                  data.message.includes('次数') || 
                  data.message.includes('限制') || 
                  data.message.includes('会话过期') || 
                  data.message.includes('请重新创建')) {
                // 特殊处理会话限制错误
                this.handleSessionLimitError(data.message);
              } else {
                // 其他一般错误
                alert(data.message || '发送追问失败');
              }
            }
            else throw new Error(data.message || '发送追问失败');
          }
        })
        .catch(error => {
          this.followUpStatus = 'error';
          console.error('追问错误:', error);
          
          // 检查错误信息，判断是否是会话限制
          if (error.message && (
              error.message.includes('会话超过限制') || 
              error.message.includes('次数') || 
              error.message.includes('限制') || 
              error.message.includes('会话过期') || 
              error.message.includes('请重新创建'))) {
            // 特殊处理会话限制错误
            this.handleSessionLimitError(error.message);
          } else {
            // 恢复原始内容
            this.analysisResult = originalContent;
            
            // 附加错误消息
            setTimeout(() => {
              const errorHtml = marked('\n\n---\n\n**错误**：发送追问失败，请重试', { 
                breaks: true, 
                gfm: true 
              });
              this.analysisResult += errorHtml;
              
              // 恢复滚动位置
              if (analysisContent) {
                analysisContent.scrollTop = scrollPosition;
              }
            }, 100);
          }
          
          // 延迟重置状态，以便用户可以再次尝试
          setTimeout(() => {
            this.followUpStatus = null;
          }, 2000);
        });
    },
    
    // 处理会话限制错误
    handleSessionLimitError(errorMessage) {
      // 显示友好的错误提示
      const friendlyMessage = '会话超过限制次数或已过期，请重新创建会话。';
      
      // 弹窗提示用户
      alert(friendlyMessage);
      
      // 禁用追问功能，提示用户重新开始
      this.isContentFullyDisplayed = true;
      
      // 重置状态
      this.followUpStatus = null;
    },
    
    pollFollowUpResponse() {
      if (!this.analysisFilePath) {
        console.error('轮询错误: 分析文件路径为空');
        return;
      }
      
      // 轮询开始时设置标记为false
      this.isContentFullyDisplayed = false;
      
      let lastContentLength = 0;
      let pollCount = 0;
      let lastUpdateTime = Date.now();
      let emptyContentCount = 0;
      const maxIdleTime = 10000; // 最大空闲时间为10秒
      const maxPollCount = 60; // 最大轮询次数为60次
      
      const fetchResponseFile = () => {
        pollCount++;
        console.log(`[追问响应] 轮询第 ${pollCount} 次`);
        
        // 每次请求都添加强制刷新参数，确保获取最新内容
        const forceRefresh = true;
        
        AIService.getAnalysisFile(this.analysisFilePath, forceRefresh)
          .then(data => {
            if (data) {
              // 优先检查完成状态
              const finishStatus = data.finish_status || {};
              const isFinished = finishStatus.finished || false;
              const finishReason = finishStatus.finish_reason || null;
              const hasResponse = finishStatus.has_response || false;
              
              // 根据后端返回的完成状态直接判断是否结束轮询
              if (isFinished || hasResponse) {
                console.log(`[追问响应] 后端返回已完成标志，原因: ${finishReason || '完成响应'}`);
                
                // 立即获取最终内容并设置已完成标记
                AIService.getAnalysisFile(this.analysisFilePath, true)
                  .then(finalData => {
                    if (finalData && finalData.content) {
                      const fullHtml = marked(finalData.content, {
                        breaks: true,
                        gfm: true,
                        headerIds: false,
                        mangle: false
                      });
                      this.analysisResult = fullHtml;
                      
                      // 设置内容已完全展示的标记
                      this.isContentFullyDisplayed = true;
                      this.followUpStatus = null;
                      console.log('[追问响应] 内容已完全展示，可以发送追问');
                      
                      // 添加自动滚动到底部的功能
                      this.$nextTick(() => {
                        const analysisContent = document.querySelector('.analysis-content');
                        if (analysisContent) {
                          analysisContent.scrollTop = analysisContent.scrollHeight;
                        }
                      });
                    }
                  })
                  .catch(err => console.error('获取最终内容失败:', err.message));
                
                return;
              }
              
              // 如果响应中包含'unchanged'字段，表示文件内容未变化
              if (data.unchanged) {
                // 更新最后轮询时间的差值
                const idleTime = Date.now() - lastUpdateTime;
                
                // 如果长时间未更新(超过5秒)，判断为可能已完成
                if (idleTime > 5000 && pollCount > 5) {
                  console.log(`[追问响应] 内容长时间未变化(${Math.floor(idleTime/1000)}秒)，可能已完成`);
                  
                  // 再次请求获取最终内容
                  AIService.getAnalysisFile(this.analysisFilePath, true)
                    .then(finalData => {
                      if (finalData && finalData.content) {
                        const fullHtml = marked(finalData.content, {
                          breaks: true,
                          gfm: true,
                          headerIds: false,
                          mangle: false
                        });
                        this.analysisResult = fullHtml;
                        
                        // 设置内容已完全展示的标记
                        this.isContentFullyDisplayed = true;
                        this.followUpStatus = null;
                        console.log('[追问响应] 内容长时间未变化，标记为已完成');
                        
                        // 添加自动滚动到底部的功能
                        this.$nextTick(() => {
                          const analysisContent = document.querySelector('.analysis-content');
                          if (analysisContent) {
                            analysisContent.scrollTop = analysisContent.scrollHeight;
                          }
                        });
                      }
                    });
                  return;
                }
                
                let shouldContinuePolling = pollCount < maxPollCount && idleTime < maxIdleTime;
                if (shouldContinuePolling) {
                  // 增加轮询间隔，减少请求频率
                  const pollInterval = Math.min(1000 + Math.floor(idleTime / 1000) * 200, 3000);
                  setTimeout(fetchResponseFile, pollInterval);
                } else {
                  // 超时结束轮询
                  console.log(`[追问响应] 轮询结束: 总计${pollCount}次，空闲${Math.floor(idleTime/1000)}秒`);
                  this.followUpStatus = null;
                  this.isContentFullyDisplayed = true;
                }
                return;
              }
              
              // 处理有内容更新的情况
              const currentContent = data.content;
              const isBeingWritten = data.is_being_written || false;
              
              if (currentContent.length === 0) {
                emptyContentCount++;
              } else {
                emptyContentCount = 0;
              }
              
              const hasNewContent = currentContent.length > lastContentLength;
              
              // 如果有新内容，更新最后更新时间和UI
              if (hasNewContent || pollCount === 1) {
                lastUpdateTime = Date.now();
                console.log(`[追问响应] 检测到内容更新，长度: ${currentContent.length}`);
                
                try {
                  // 尝试提取AI回复部分
                  let formattedContent = currentContent;
                  const userMsgIndex = formattedContent.lastIndexOf(`我：${this.followUpMessage}`);
                  
                  if (userMsgIndex !== -1) {
                    const deepseekIndex = formattedContent.indexOf("DEEPSEEK：", userMsgIndex);
                    if (deepseekIndex !== -1) {
                      // 获取DEEPSEEK的回复部分
                      const responseText = formattedContent.substring(deepseekIndex + 9).trim();
                      
                      // 查找思考中...的HTML元素
                      const thinkingPattern = /<em>思考中\.\.\.<\/em>/;
                      
                      // 替换思考中...为实际回复的内容，正确处理Markdown格式
                      if (this.analysisResult.includes('<em>思考中...</em>')) {
                        // 使用marked库直接将Markdown转换为HTML
                        const responseHtml = marked(responseText, {
                          breaks: true,
                          gfm: true,
                          headerIds: false,
                          mangle: false
                        });
                        
                        // 替换思考中...为实际回复
                        this.analysisResult = this.analysisResult.replace(thinkingPattern, responseHtml);
                        
                        // 添加自动滚动到底部的功能
                        this.$nextTick(() => {
                          const analysisContent = document.querySelector('.analysis-content');
                          if (analysisContent) {
                            analysisContent.scrollTop = analysisContent.scrollHeight;
                          }
                        });
                      } else {
                        // 如果无法找到思考中...标记，尝试使用完整内容重建响应
                        // 处理全部内容的Markdown格式
                        const fullHtml = marked(formattedContent, {
                          breaks: true,
                          gfm: true,
                          headerIds: false,
                          mangle: false
                        });
                        
                        // 直接使用完整的HTML内容
                        this.analysisResult = fullHtml;
                        
                        // 添加自动滚动到底部的功能
                        this.$nextTick(() => {
                          const analysisContent = document.querySelector('.analysis-content');
                          if (analysisContent) {
                            analysisContent.scrollTop = analysisContent.scrollHeight;
                          }
                        });
                      }
                    } else {
                      // 第一次轮询或内容显著变更时，直接使用完整内容
                      if (pollCount === 1 || (currentContent.length - lastContentLength) > 50) {
                        const fullHtml = marked(formattedContent, {
                          breaks: true,
                          gfm: true,
                          headerIds: false,
                          mangle: false
                        });
                        this.analysisResult = fullHtml;
                        
                        // 添加自动滚动到底部的功能
                        this.$nextTick(() => {
                          const analysisContent = document.querySelector('.analysis-content');
                          if (analysisContent) {
                            analysisContent.scrollTop = analysisContent.scrollHeight;
                          }
                        });
                      }
                    }
                  } else {
                    // 第一次轮询或者内容发生明显变化时，直接使用完整内容
                    if (pollCount === 1 || (currentContent.length - lastContentLength) > 50) {
                      const fullHtml = marked(formattedContent, {
                        breaks: true,
                        gfm: true,
                        headerIds: false,
                        mangle: false
                      });
                      this.analysisResult = fullHtml;
                      
                      // 添加自动滚动到底部的功能
                      this.$nextTick(() => {
                        const analysisContent = document.querySelector('.analysis-content');
                        if (analysisContent) {
                          analysisContent.scrollTop = analysisContent.scrollHeight;
                        }
                      });
                    }
                  }
                } catch (error) {
                  console.error('解析回复内容时出错:', error.message);
                  // 出错时直接使用完整内容
                  try {
                    const fullHtml = marked(currentContent, {
                      breaks: true,
                      gfm: true,
                      headerIds: false,
                      mangle: false
                    });
                    this.analysisResult = fullHtml;
                    
                    // 添加自动滚动到底部的功能
                    this.$nextTick(() => {
                      const analysisContent = document.querySelector('.analysis-content');
                      if (analysisContent) {
                        analysisContent.scrollTop = analysisContent.scrollHeight;
                      }
                    });
                  } catch (err) {
                    console.error('处理完整内容时再次出错:', err.message);
                  }
                }
                
                lastContentLength = currentContent.length;
              }
            }
            
            const idleTime = Date.now() - lastUpdateTime;
            
            // 判断是否应该继续轮询
            let shouldContinuePolling = false;
            
            if (isFinished) {
              // 如果元数据标记已完成，停止轮询
              shouldContinuePolling = false;
            } else if (pollCount >= maxPollCount) {
              // 达到最大轮询次数
              shouldContinuePolling = false;
            } else if (isBeingWritten) {
              // 文件仍在被写入
              shouldContinuePolling = true;
            } else if (idleTime < maxIdleTime) {
              // 在空闲时间内，但根据空闲时间长短调整轮询频率
              shouldContinuePolling = true;
            } else if (currentContent.length === 0 && emptyContentCount < 5) {
              // 内容为空但次数较少(减少了阈值)
              shouldContinuePolling = true;
            } else {
              // 默认行为 - 未捕获的条件
              shouldContinuePolling = false;
            }
            
            if (shouldContinuePolling) {
              // 根据空闲时间调整轮询间隔，减少不必要的请求
              const pollInterval = hasNewContent ? 200 : Math.min(500 + Math.floor(idleTime / 1000) * 100, 1500);
              setTimeout(fetchResponseFile, pollInterval);
            } else {
              console.log(`[追问响应] 轮询结束: 总共${pollCount}次，原因: ${isFinished ? finishReason : '超时/达到轮询上限'}`);
              this.followUpStatus = null;
              
              // 确保在轮询结束时获取一次最新完整内容
              AIService.getAnalysisFile(this.analysisFilePath, true)
                .then(finalData => {
                  if (finalData && finalData.content) {
                    const fullHtml = marked(finalData.content, {
                      breaks: true,
                      gfm: true,
                      headerIds: false,
                      mangle: false
                    });
                    this.analysisResult = fullHtml;
                    
                    // 设置内容已完全展示的标记
                    this.isContentFullyDisplayed = true;
                    console.log('[追问响应] 内容已完全展示，可以发送追问');
                    
                    // 添加自动滚动到底部的功能
                    this.$nextTick(() => {
                      const analysisContent = document.querySelector('.analysis-content');
                      if (analysisContent) {
                        analysisContent.scrollTop = analysisContent.scrollHeight;
                      }
                    });
                  }
                })
                .catch(err => console.error('获取最终内容失败:', err.message));
              
              // 如果轮询结束但仍显示"思考中..."，替换为适当消息
              if (this.analysisResult.includes('<em>思考中...</em>')) {
                this.analysisResult = this.analysisResult.replace(
                  '<em>思考中...</em>', 
                  '<span style="color: #10b981;">回复已完成</span>'
                );
                // 即使替换了思考中...也标记为完成
                this.isContentFullyDisplayed = true;
              }
            }
          })
          .catch((error) => {
            console.error('获取回复时发生错误:', error.message);
            
            if (pollCount < 10) {
              setTimeout(fetchResponseFile, 1000);
            } else {
              this.followUpStatus = null;
              // 从分析结果中移除"思考中..."
              if (this.analysisResult.includes('<em>思考中...</em>')) {
                this.analysisResult = this.analysisResult.replace(
                  '<em>思考中...</em>', 
                  '<span style="color: #ef4444;">获取响应失败，请重试</span>'
                );
              }
              this.isContentFullyDisplayed = true;
            }
          });
      };
      
      // 开始轮询前稍微延迟，确保分析文件已更新
      setTimeout(fetchResponseFile, 250);
    },
    
    startThinkingTimer() {
      // 初始化思考时长并开始计时
      this.thinkingTime = 0;
      this.thinkingInterval = setInterval(() => {
        this.thinkingTime++;
      }, 1000);
    },
    
    submitCustomProblem() {
      if (!this.customProblem.trim()) return;
      
      this.isSubmittingCustomProblem = true;
      
      AIService.submitCustomProblem({ problem: this.customProblem })
        .then(data => {
          if (data && data.success) {
            // 更新当前页面的题目内容
            this.problem = this.customProblem;
            this.problemSource = '自定义题目';  // 标记为自定义题目
            
            // 保存题目ID，以便后续提交录音时使用
            if (data.problem && data.problem.id) {
              this.problemId = data.problem.id;
              console.log('保存自定义题目ID:', this.problemId);
            }
            
            // 清空输入框并关闭模态框
            this.customProblem = '';
            this.showCustomProblemModal = false;
            this.isSubmittingCustomProblem = false;
            
            // 重置录音和分析状态
            this.resetRecordingAndAnalysis();
          } else {
            this.isSubmittingCustomProblem = false;
            alert('提交自定义题目失败: ' + (data.message || '请重试'));
          }
        })
        .catch(error => {
          console.error('提交自定义题目出错:', error);
          this.isSubmittingCustomProblem = false;
          alert('提交自定义题目时出错: ' + (error.message || '请重试'));
        });
    },
    
    // 重置录音和分析状态的辅助方法
    resetRecordingAndAnalysis() {
      // 重置录音相关状态
      this.isRecording = false;
      this.isPaused = false;
      this.recordingTime = 0;
      
      // 清除音频相关数据
      this.audioChunks = [];
      this.recorder = null;
      
      // 停止计时器
      if (this.recordingInterval) {
        clearInterval(this.recordingInterval);
        this.recordingInterval = null;
      }
      
      // 重新开始思考时长计时
      this.stopThinkingTimer();
      this.startThinkingTimer();
      
      // 重置分析状态
      this.analysisStatus = null;
      this.analysisResult = '';
      this.analysisError = '';
      this.statusFilePath = '';
      this.analysisFilePath = '';
      
      // 重置追问相关状态
      this.followUpMessage = '';
      this.followUpStatus = null;
      this.isContentFullyDisplayed = false;
      
      // 隐藏聊天输入框
      this.showChatInput = false;
    },
    
    // 添加页面交互处理方法
    handlePageInteraction(event) {
      // 检查用户是否已经交互过（避免重复弹出登录框）
      if (this._userInteracted) return;
      
      // 标记用户已交互
      this._userInteracted = true;
      
      // 移除点击事件监听器，避免重复处理
      document.removeEventListener('click', this.handlePageInteraction);
      
      // 检查用户登录状态
      const user = localStorage.getItem('user');
      if (!user) {
        window.dispatchEvent(new CustomEvent('auth-error', { 
          detail: { loginRequired: true, message: '请先登录以继续操作' } 
        }));
      } else {
        // 如果用户已登录，则可以检查VIP状态
        this.checkVipStatus();
      }
    },
    
    // 修改VIP检查通用方法
    checkVipAndExecute(callback) {
      // 检查用户是否已登录
      const user = localStorage.getItem('user');
      if (!user) {
        window.dispatchEvent(new CustomEvent('auth-error', { 
          detail: { loginRequired: true, message: '请先登录以继续操作' } 
        }));
        return;
      }
      
      // 如果已经是VIP，直接执行
      if (this.isVip) {
        callback();
        return;
      }
      
      // 如果VIP状态尚未检查完成，先检查
      if (!this.vipChecked) {
        this.vipChecked = true;
        
        UserService.checkVipStatus()
          .then(data => {
            this.isVip = data.isVip;
            
            // 如果是VIP，执行回调
            if (this.isVip) {
              callback();
            } else {
              // 如果不是VIP，显示提示并显示VIP模态框
              alert('该功能仅限VIP用户使用，请开通VIP会员！');
              
              // 显示VIP模态框
              if (this.$root.eventBus) {
                this.$root.eventBus.emit('show_vip_modal');
              }
            }
          })
          .catch(error => {
            // 忽略认证错误，因为这些错误会由request.js统一处理
            if (error.isAuthError) {
              console.log('忽略认证错误:', error.message);
              return;
            }
              
            if (error.statusCode === 401 && error.loginRequired) {
              console.log('用户未登录，显示登录模态窗口');
              window.dispatchEvent(new CustomEvent('auth-error', { 
                detail: { loginRequired: true, message: '请先登录以继续操作' } 
              }));
            }else{
              console.error('检查VIP状态出错:', error);
              alert('检查VIP状态时出错，请稍后重试');
            }
          });
      } else {
        // VIP状态已检查，但不是VIP，显示提示并显示VIP模态框
        alert('该功能仅限VIP用户使用，请开通VIP会员！');
        
        // 显示VIP模态框
        if (this.$root.eventBus) {
          this.$root.eventBus.emit('show_vip_modal');
        }
      }
    },
    
    // 更新分析内容的方法，使用标准样式
    updateAnalysisContent(message) {
      console.log("更新分析内容:", message);
      
      // 创建状态消息的HTML
      const statusMessageHtml = `<div class="status-message">${message}</div>`;
      
      // 如果是要替换内容而不是追加，则直接设置
      if (!this.analysisResult || this.analysisResult.trim() === '') {
        this.analysisResult = statusMessageHtml;
      } else {
        // 如果内容已经是HTML格式，直接添加新的状态消息HTML
        this.analysisResult += statusMessageHtml;
      }
      
      // 添加自动滚动到底部的功能
      this.$nextTick(() => {
        const analysisContent = document.querySelector('.analysis-content');
        if (analysisContent) {
          analysisContent.scrollTop = analysisContent.scrollHeight;
        }
      });
    },
    
    // 添加停止思考计时器的方法
    stopThinkingTimer() {
      // 停止思考时长计时器
      if (this.$refs.thinkingTimer) {
        this.$refs.thinkingTimer.pause();
      }
      
      // 清除思考时长计时器interval
      if (this.thinkingInterval) {
        clearInterval(this.thinkingInterval);
        this.thinkingInterval = null;
      }
      
      console.log('思考时长计时器已停止，记录时间：', this.thinkingTime, '秒');
    },
    
    // 手动触发发送
    manualSend() {
      // 获取输入框中的内容
      const inputElement = document.querySelector('.chat-input');
      let content = '';
      
      if (inputElement) {
        content = inputElement.value;
      }
      
      // 优先使用DOM中的值，其次使用组件数据
      const messageToSend = content || this.followUpMessage;
      
      if (messageToSend && messageToSend.trim()) {
        // 先更新数据模型
        this.followUpMessage = messageToSend;
        
        // 直接调用处理函数，跳过所有检查
        this.proceedWithFollowUp();
        
        // 手动清空输入框
        if (inputElement) {
          inputElement.value = '';
        }
      } else {
        console.log('消息为空，无法发送');
      }
    },
    clearChatInput() {
      // 方法1: 使用组件引用
      if (this.$refs.chatInput) {
        this.$refs.chatInput.clearInput();
      }
      
      // 方法2: DOM操作(备用)
      const inputElement = document.querySelector('.chat-input');
      if (inputElement) {
        inputElement.value = '';
      }
    },
    
    // 显示下一题确认对话框
    showNextQuestionConfirm() {
      this.showNextQuestionModal = true;
    },
    
    // 确认后进入下一题
    proceedToNextQuestion() {
      // 关闭确认对话框
      this.showNextQuestionModal = false;
      
      // 清空当前记录并刷新页面
      this.refreshPage();
    },
    
    // 刷新页面的方法
    refreshPage() {
      // 刷新当前页面
      window.location.reload();
    },
  }
};
</script>

<style>
/* 添加下一题按钮的样式 */
.next-question-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.btn-next-question {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.3s;
}

.btn-next-question:hover {
  background-color: #45a049;
}

.btn-next-question svg {
  width: 16px;
  height: 16px;
}

/* 已有的模态框CSS会应用到确认对话框 */
</style>

/* 轮询计数器样式 */
.poll-counter {
  display: inline-block;
  background-color: rgba(25, 118, 210, 0.1);
  border: 1px solid rgba(25, 118, 210, 0.3);
  color: #1976d2;
  border-radius: 4px;
  padding: 4px 10px;
  margin: 10px 0;
  font-size: 14px;
  text-align: center;
}

[data-theme="dark"] .poll-counter {
  background-color: rgba(66, 165, 245, 0.1);
  border-color: rgba(66, 165, 245, 0.3);
  color: #42a5f5;
}
